var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as PIXI from "pixi.js";
import * as React from "react";
import { render, unmountComponentAtNode } from "react-dom";
import { NoteUITrack, SoundFileUITrack } from "../Shared/UITrack.js";
import { UIColors, UIPositioning } from "../Settings/UITheme.js";
import { BoxSelect, ClickOutsideWatcher, FAButton, IconFileInput, LabelledCheckbox, Slider } from "../SharedReact/BasicElements.js";
import { SimpleEvent } from "../../HelperModules/SimpleEvent.js";
import { UITrackStore } from "../ReactUI/UITrackStore.js";
import { LoadingErrorModal } from "../SharedReact/Modal.js";
const modalRoot = document.getElementById("root");
export class TrackList extends PIXI.Container {
    constructor(sidebarWidth, width, height, songManager) {
        super();
        this._verticalScroll = 0;
        this.songManager = songManager;
        this._sidebarWidth = sidebarWidth;
        this.trackEdited = new SimpleEvent();
        this._gainChanged = this._gainChanged.bind(this);
        this._nameChanged = this._nameChanged.bind(this);
        this._soundFileChanged = this._soundFileChanged.bind(this);
        this._allowOverlapChanged = this._allowOverlapChanged.bind(this);
        this._displayActualWidthChanged = this._displayActualWidthChanged.bind(this);
        this._updateSoundFile = this._updateSoundFile.bind(this);
        this._deleteTrack = this._deleteTrack.bind(this);
        this._connectionChanged = this._connectionChanged.bind(this);
        this.drawTracks = this.drawTracks.bind(this);
        this._envelopeEnabledChanged = this._envelopeEnabledChanged.bind(this);
        this._envelopeAttackChanged = this._envelopeAttackChanged.bind(this);
        this._envelopeReleaseChanged = this._envelopeReleaseChanged.bind(this);
        this._oscillatorTypeChanged = this._oscillatorTypeChanged.bind(this);
        this._trackListGraphics = new PIXI.Graphics();
        this._trackLineGraphics = new PIXI.Graphics();
        this.addChild(this._trackListGraphics, this._trackLineGraphics);
        this._trackSettingsContainer = document.createElement("div");
        this._trackSettingsContainer.style.position = "absolute";
        this._trackSettingsContainer.style.width = this._sidebarWidth.toString() + "px";
        this._trackSettingsContainer.style.overflow = "hidden";
        document.getElementById("applicationContainer").appendChild(this._trackSettingsContainer);
        this.resize(width, height);
        this._cleanupListener = UITrackStore.subscribe(this.drawTracks);
    }
    resize(width, height) {
        this.endX = width;
        this.endY = height;
        this._trackListGraphics.clear().beginFill(UIColors.bgColor)
            .drawRect(0, 0, this._sidebarWidth, this.endY)
            .endFill();
        this._trackListGraphics.beginFill(UIColors.fgColor)
            .drawRect(this._sidebarWidth - 2, 0, 3, this.endY)
            .drawRect(0, UIPositioning.timelineHeaderHeight, this._sidebarWidth, 2)
            .endFill();
        this.drawTracks();
    }
    updateVerticalScroll(value) {
        this._trackLineGraphics.y = value;
        this._verticalScroll = value;
        this._rerenderList();
    }
    destroy() {
        unmountComponentAtNode(this._trackSettingsContainer);
        this._cleanupListener();
        super.destroy({ children: true });
    }
    addedHandler() {
        this._trackSettingsContainer.style.removeProperty("display");
    }
    removedHandler() {
        this._trackSettingsContainer.style.display = "none";
    }
    drawTracks() {
        let tracks = UITrackStore.getState().tracks;
        this._trackLineGraphics.clear();
        if (tracks.length > 0) {
            this._trackLineGraphics.beginFill(UIColors.fgColor);
            tracks.forEach(track => {
                this._trackLineGraphics.drawRect(0, track.startY + track.height, this.endX, 2);
            });
            this._trackLineGraphics.endFill();
            this._trackLineGraphics.mask = new PIXI.Graphics().beginFill(0xFFFFFF).drawRect(0, UIPositioning.timelineHeaderHeight, this.endX, this.endY).endFill();
            this._trackSettingsContainer.style.top = tracks[0].startY.toString() + "px";
            this._trackSettingsContainer.style.height = (this.endY - tracks[0].startY).toString() + "px";
            this._rerenderList();
        }
        else {
            unmountComponentAtNode(this._trackSettingsContainer);
        }
    }
    _nameChanged(index, value) {
        let tracks = UITrackStore.getState().tracks;
        tracks[index].name = value;
        this._rerenderList();
    }
    _gainChanged(index, value) {
        let tracks = UITrackStore.getState().tracks;
        tracks[index].track.audioSource.masterGain = value;
        this._rerenderList();
    }
    _soundFileChanged(index, files) {
        let modalDiv = document.createElement("div");
        modalRoot.appendChild(modalDiv);
        render(React.createElement(LoadingErrorModal, { loading: true, title: "Loading Sound File...", onClose: () => { unmountComponentAtNode(modalDiv); } }), modalDiv);
        let file = files[0];
        const objecturl = URL.createObjectURL(file);
        var xhr = new XMLHttpRequest();
        xhr.open('GET', objecturl, true);
        xhr.responseType = 'blob';
        let onloadFunc = this._updateSoundFile;
        xhr.onload = function (e) {
            if (this.status == 200) {
                onloadFunc(index, this.response);
                unmountComponentAtNode(modalDiv);
                modalRoot.removeChild(modalDiv);
            }
            else {
                render(React.createElement(LoadingErrorModal, { loading: false, title: "Unknown error. Please try again.", onClose: () => { unmountComponentAtNode(modalDiv); modalRoot.removeChild(modalDiv); } }), modalDiv);
            }
        };
        xhr.send();
    }
    _updateSoundFile(index, blob) {
        return __awaiter(this, void 0, void 0, function* () {
            let tracks = UITrackStore.getState().tracks;
            let soundFileTrack = tracks[index].track;
            yield soundFileTrack.setSoundFile(blob);
            this.trackEdited.emit(index);
        });
    }
    _allowOverlapChanged(index, value) {
        let tracks = UITrackStore.getState().tracks;
        let soundFileTrack = tracks[index].track;
        soundFileTrack.allowOverlaps = value;
        this._rerenderList();
    }
    _displayActualWidthChanged(index, value) {
        let tracks = UITrackStore.getState().tracks;
        let soundFileTrack = tracks[index];
        soundFileTrack.displayActualWidth = value;
        this.trackEdited.emit(index);
        this._rerenderList();
    }
    _connectionChanged(index, connectionIndex) {
        let tracks = UITrackStore.getState().tracks;
        tracks[index].track.connectTo(tracks[index].track.possibleConnections[connectionIndex]);
        this._rerenderList();
    }
    _deleteTrack(index) {
        let tracks = UITrackStore.getState().tracks;
        this.songManager.removeTrack(tracks[index].track);
        UITrackStore.dispatch({ type: "REMOVE_TRACK", index: index });
    }
    _oscillatorTypeChanged(index, value) {
        let track = UITrackStore.getState().tracks[index];
        if (track instanceof NoteUITrack) {
            track.track.audioSource.oscillatorType = value.toLowerCase();
            this._rerenderList();
        }
    }
    _envelopeEnabledChanged(index, value) {
        let track = UITrackStore.getState().tracks[index];
        if (track instanceof NoteUITrack) {
            track.track.audioSource.envelopeEnabled = value;
            this._rerenderList();
        }
    }
    _envelopeAttackChanged(index, value) {
        let track = UITrackStore.getState().tracks[index];
        if (track instanceof NoteUITrack) {
            value = Math.max(0, Math.min(4, value));
            track.track.audioSource.envelope.attack = value;
            this._rerenderList();
        }
    }
    _envelopeReleaseChanged(index, value) {
        let track = UITrackStore.getState().tracks[index];
        if (track instanceof NoteUITrack) {
            value = Math.max(0, Math.min(4, value));
            track.track.audioSource.envelope.release = value;
            this._rerenderList();
        }
    }
    _rerenderList() {
        let tracks = UITrackStore.getState().tracks;
        if (tracks.length > 0) {
            render(React.createElement(TrackSettingsList, { tracks: tracks, possibleConnections: this.songManager.connectionManager.possibleConnectionStrings, onNameChange: this._nameChanged, onGainChange: this._gainChanged, onSoundFileUpdate: this._soundFileChanged, onAllowOverlapChange: this._allowOverlapChanged, onDisplayActualWidthChange: this._displayActualWidthChanged, onDeleteTrack: this._deleteTrack, onConnectionChanged: this._connectionChanged, width: this._sidebarWidth, verticalScroll: this._verticalScroll, onOscillatorTypeChanged: this._oscillatorTypeChanged, onEnvelopeEnabledChanged: this._envelopeEnabledChanged, onEnvelopeAttackChanged: this._envelopeAttackChanged, onEnvelopeReleaseChanged: this._envelopeReleaseChanged }), this._trackSettingsContainer);
        }
    }
}
class TrackSettingsList extends React.Component {
    render() {
        return (React.createElement("div", { style: { position: "absolute", top: this.props.verticalScroll.toString() + "px" } }, this.props.tracks.map((track, index) => {
            if (track instanceof SoundFileUITrack) {
                return React.createElement(SoundFileTrackSettingsBox, { key: index, index: index, name: track.name, onNameChange: this.props.onNameChange, gain: track.track.audioSource.masterGain, onGainChange: this.props.onGainChange, deleteTrack: this.props.onDeleteTrack, width: this.props.width, height: track.height, soundFileChange: this.props.onSoundFileUpdate, displayActualWidth: track.displayActualWidth, displayActualWidthChange: this.props.onDisplayActualWidthChange, allowOverlap: track.track.allowOverlaps, allowOverlapChange: this.props.onAllowOverlapChange, connection: track.track.connection, connectionOptions: this.props.possibleConnections, connectionChanged: this.props.onConnectionChanged });
            }
            else if (track instanceof NoteUITrack) {
                let instrument = track.track.audioSource;
                let oscillatorProps = {
                    enabled: instrument.envelopeEnabled,
                    enabledChanged: this.props.onEnvelopeEnabledChanged,
                    attack: instrument.envelope.attack,
                    attackChanged: this.props.onEnvelopeAttackChanged,
                    release: instrument.envelope.release,
                    releaseChanged: this.props.onEnvelopeReleaseChanged
                };
                return React.createElement(OscillatorTrackSettingsBox, { key: index, index: index, name: track.name, onNameChange: this.props.onNameChange, gain: track.track.audioSource.masterGain, onGainChange: this.props.onGainChange, deleteTrack: this.props.onDeleteTrack, width: this.props.width, height: track.height, oscillatorType: capitalise(track.track.audioSource.oscillatorType), oscillatorTypeChanged: this.props.onOscillatorTypeChanged, envelopeSettings: oscillatorProps, connection: track.track.connection, connectionOptions: this.props.possibleConnections, connectionChanged: this.props.onConnectionChanged });
            }
        })));
    }
}
class SoundFileTrackSettingsBox extends React.PureComponent {
    constructor(props) {
        super(props);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleGainChange = this.handleGainChange.bind(this);
        this.handleConnectionChanged = this.handleConnectionChanged.bind(this);
    }
    handleNameChange(value) {
        this.props.onNameChange(this.props.index, value);
    }
    handleGainChange(value) {
        this.props.onGainChange(this.props.index, value);
    }
    handleConnectionChanged(index) {
        this.props.connectionChanged(this.props.index, index);
    }
    render() {
        return React.createElement("div", { style: { position: "relative" } },
            React.createElement(FAButton, { className: "trackSettingsDeleteButton buttonColorAnim", title: "Delete Track", iconName: "fa fa-close", onClick: () => { this.props.deleteTrack(this.props.index); } }),
            React.createElement("div", { className: "trackSettingsDiv", style: { width: this.props.width, height: this.props.height } },
                React.createElement("input", { className: "trackSettingsName", type: "text", value: this.props.name, size: Math.max(1, this.props.name.length), onChange: (event) => { this.handleNameChange(event.target.value); } }),
                React.createElement(Slider, { min: 0, max: 1, step: 0.01, value: this.props.gain, onChange: this.handleGainChange }),
                React.createElement(IconFileInput, { className: "trackSettingsIconInput buttonColorAnim", title: "Set Sound File", iconName: "fa fa-music", onChange: (files) => { this.props.soundFileChange(this.props.index, files); }, accept: "audio/*" }),
                React.createElement(LabelledCheckbox, { className: "trackSettingsInteractable pointer", label: "Display Actual Width:", state: this.props.displayActualWidth, onChange: (value) => { this.props.displayActualWidthChange(this.props.index, value); } }),
                React.createElement(LabelledCheckbox, { className: "trackSettingsInteractable pointer", label: "Allow Overlaps:", state: this.props.allowOverlap, onChange: (value) => { this.props.allowOverlapChange(this.props.index, value); } }),
                React.createElement(BoxSelect, { className: "trackSettingsBoxSelect", boxSelectTitle: this.props.connection, options: this.props.connectionOptions, selectedCallback: this.handleConnectionChanged })));
    }
}
class OscillatorTrackSettingsBox extends React.PureComponent {
    constructor(props) {
        super(props);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleGainChange = this.handleGainChange.bind(this);
        this.handleConnectionChanged = this.handleConnectionChanged.bind(this);
    }
    handleNameChange(value) {
        this.props.onNameChange(this.props.index, value);
    }
    handleGainChange(value) {
        this.props.onGainChange(this.props.index, value);
    }
    handleConnectionChanged(index) {
        this.props.connectionChanged(this.props.index, index);
    }
    render() {
        let es = this.props.envelopeSettings;
        return React.createElement("div", { style: { position: "relative" } },
            React.createElement(FAButton, { className: "trackSettingsDeleteButton buttonColorAnim", title: "Delete Effect", iconName: "fa fa-close", onClick: () => { this.props.deleteTrack(this.props.index); } }),
            React.createElement("div", { className: "trackSettingsDiv", style: { width: this.props.width, height: this.props.height } },
                React.createElement("input", { className: "trackSettingsName", type: "text", value: this.props.name, size: Math.max(1, this.props.name.length), onChange: (event) => { this.handleNameChange(event.target.value); } }),
                React.createElement(Slider, { min: 0, max: 1, step: 0.01, value: this.props.gain, onChange: this.handleGainChange }),
                React.createElement("div", { className: "trackSettingsInteractable" },
                    React.createElement("p", null, "Type:"),
                    React.createElement(BoxSelect, { boxSelectTitle: this.props.oscillatorType, options: OscillatorTrackSettingsBox.oscillatorOptions, selectedCallback: (value) => { this.props.oscillatorTypeChanged(this.props.index, OscillatorTrackSettingsBox.oscillatorOptions[value]); } })),
                React.createElement(OscillatorEnvelope, { enabled: es.enabled, enabledChanged: (value) => { es.enabledChanged(this.props.index, value); }, attack: es.attack, attackChanged: (value) => { es.attackChanged(this.props.index, value); }, release: es.release, releaseChanged: (value) => { es.releaseChanged(this.props.index, value); } }),
                React.createElement(BoxSelect, { className: "trackSettingsBoxSelect", boxSelectTitle: this.props.connection, options: this.props.connectionOptions, selectedCallback: this.handleConnectionChanged })));
    }
}
OscillatorTrackSettingsBox.oscillatorOptions = ["Sine", "Square", "Sawtooth", "Triangle"];
class OscillatorEnvelope extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            detailsVisible: false
        };
        this._openCloseButton = React.createRef();
    }
    render() {
        return React.createElement("div", { className: "oscillatorEnvelopeContainer" },
            React.createElement("button", { className: "mainBoxSelectButton", ref: this._openCloseButton, onClick: () => { this.setState({ detailsVisible: !this.state.detailsVisible }); } }, "Envelope Settings"),
            this.state.detailsVisible && React.createElement(ClickOutsideWatcher, { callback: () => this.setState({ detailsVisible: false }) },
                React.createElement(OscillatorEnvelopeDetails, Object.assign({}, this.props, { mainButton: this._openCloseButton }))));
    }
}
class OscillatorEnvelopeDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            lastAttackValue: "",
            lastReleaseValue: ""
        };
    }
    render() {
        let disabled = !this.props.enabled;
        return React.createElement("div", { className: "envelopeOverlayOuterContainer", style: { left: this.props.mainButton.current.offsetWidth + 2 } },
            React.createElement("div", { className: "envelopeOverlayInnerContainer" },
                React.createElement("div", { className: "boxSelectOverlayArrow left" }),
                React.createElement("div", { className: "boxSelectOverlay envelopeOverlayContent" },
                    React.createElement("div", { className: "effectProperty" },
                        React.createElement("p", null, "Enabled:"),
                        React.createElement("input", { type: "checkbox", checked: this.props.enabled, onChange: (event) => { this.props.enabledChanged(event.target.checked); } })),
                    React.createElement("div", { className: "effectProperty" },
                        React.createElement("p", null, "Attack:"),
                        React.createElement("input", { type: "number", step: "0.01", value: (parseFloat(this.state.lastAttackValue) === this.props.attack ? this.state.lastAttackValue : this.props.attack.toString()), onChange: (event) => { this.setState({ lastAttackValue: event.target.value }); this.props.attackChanged(event.target.value); }, disabled: disabled })),
                    React.createElement("div", { className: "effectProperty" },
                        React.createElement("p", null, "Release:"),
                        React.createElement("input", { type: "number", step: "0.01", value: (parseFloat(this.state.lastReleaseValue) === this.props.release ? this.state.lastReleaseValue : this.props.release.toString()), onChange: (event) => { this.setState({ lastReleaseValue: event.target.value }); this.props.releaseChanged(event.target.value); }, disabled: disabled })))));
    }
}
function capitalise(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
//# sourceMappingURL=TrackList.js.map